/**
 * <lightweight-accordion>
 *
 * A small, dependency-free accordion. Reusable anywhere in the theme.
 *
 *   <lightweight-accordion data-single-open-mobile>
 *     <div class="accordion-item">
 *       <button class="accordion-item__trigger" aria-expanded="false">…</button>
 *       <div class="accordion-item__panel">…</div>
 *     </div>
 *     …
 *   </lightweight-accordion>
 *
 * Attributes (single-open behaviour, pick at most one):
 *   data-single-open         — always allow only one open item.
 *   data-single-open-mobile  — allow only one open item on mobile; multiple on desktop.
 *   (none)                   — always allow multiple open items.
 *
 * Item markup contract:
 *   .accordion-item             wrapper (gets .is-open when expanded)
 *   .accordion-item__trigger    clickable header (button, toggles aria-expanded)
 *   .accordion-item__panel      collapsible content
 */
if (!customElements.get('lightweight-accordion')) {
  customElements.define(
    'lightweight-accordion',
    class LightweightAccordion extends HTMLElement {
      connectedCallback() {
        this.alwaysSingle = this.hasAttribute('data-single-open');
        this.singleOnMobile = this.hasAttribute('data-single-open-mobile');
        this.mobileQuery = window.matchMedia('(max-width: 749px)');

        this.addEventListener('click', this.onClick.bind(this));

        // When shrinking to mobile in single-on-mobile mode, collapse to one open item.
        if (this.singleOnMobile) {
          this.onBreakpointChange = this.onBreakpointChange.bind(this);
          this.mobileQuery.addEventListener('change', this.onBreakpointChange);
        }
      }

      disconnectedCallback() {
        if (this.singleOnMobile) {
          this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
        }
      }

      get isSingleOpen() {
        if (this.alwaysSingle) return true;
        if (this.singleOnMobile) return this.mobileQuery.matches;
        return false;
      }

      onClick(event) {
        const trigger = event.target.closest('.accordion-item__trigger');
        if (!trigger || !this.contains(trigger)) return;

        const item = trigger.closest('.accordion-item');
        if (!item) return;

        this.toggle(item, trigger);
      }

      toggle(item, trigger) {
        const isOpen = item.classList.contains('is-open');

        if (this.isSingleOpen && !isOpen) {
          this.closeAll(item);
        }

        if (isOpen) {
          this.close(item, trigger);
        } else {
          this.open(item, trigger);
        }
      }

      open(item, trigger) {
        item.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      }

      close(item, trigger) {
        item.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      }

      closeAll(except) {
        this.querySelectorAll('.accordion-item.is-open').forEach((item) => {
          if (item === except) return;
          const trigger = item.querySelector('.accordion-item__trigger');
          this.close(item, trigger);
        });
      }

      // Just entered mobile: keep only the first open item expanded.
      onBreakpointChange() {
        if (!this.mobileQuery.matches) return;
        const openItems = this.querySelectorAll('.accordion-item.is-open');
        openItems.forEach((item, index) => {
          if (index === 0) return;
          const trigger = item.querySelector('.accordion-item__trigger');
          this.close(item, trigger);
        });
      }
    }
  );
}
